/**
 * WorkspaceRecentQuestions
 *
 * Shows the 5 most recent (deduplicated) questions in a scrollable list.
 * "View all" opens a modal with full list, search, and status filter.
 * Clicking any question auto-posts it to On the Record.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowUpRight, X, Search, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { RecentQuestion } from '@/types/workspaceDetail';

interface WorkspaceRecentQuestionsProps {
  questions: RecentQuestion[];
  loading: boolean;
  workspaceId: string;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === yesterday.toDateString())
    return `Yesterday, ${time}`;

  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    `, ${time}`
  );
}

const STATUS_FILTERS = ['All', 'completed', 'failed', 'running'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

// ── Modal ──────────────────────────────────────────────────────────────────────

interface ModalProps {
  questions: RecentQuestion[];
  workspaceId: string;
  onClose: () => void;
  onAsk: (q: string) => void;
}

const AllQuestionsModal: React.FC<ModalProps> = ({ questions, onClose, onAsk }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const searchRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return questions.filter((item) => {
      const matchesSearch = !q || item.question.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [questions, search, statusFilter]);

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="flex w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">All Questions</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {questions.length} unique question{questions.length !== 1 ? 's' : ''} — click any to re-ask
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search + filter */}
        <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions…"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-1">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s === 'All' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Question list */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="h-8 w-8 text-gray-200 mb-3" />
              <p className="text-sm text-gray-500">No questions match your search.</p>
            </div>
          ) : (
            filtered.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => onAsk(q.question)}
                className="w-full group flex items-start justify-between gap-3 px-6 py-3.5 text-left hover:bg-blue-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 leading-snug group-hover:text-blue-700">
                    {q.question}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs text-gray-400">{formatTimestamp(q.asked_at)}</span>
                    {q.status !== 'completed' && (
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        q.status === 'failed' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {q.status}
                      </span>
                    )}
                  </div>
                </div>
                <MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300 group-hover:text-blue-400 transition-colors" />
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

export const WorkspaceRecentQuestions: React.FC<WorkspaceRecentQuestionsProps> = ({
  questions,
  loading,
  workspaceId,
}) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const handleAsk = (question: string) => {
    setModalOpen(false);
    navigate(`/tools/on-the-record?workspace=${workspaceId}`, {
      state: {
        preselectedWorkspaceId: workspaceId,
        ...(question ? { prefillQuestion: question } : {}),
      },
    });
  };

  const preview = questions.slice(0, 5);

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Recent Questions
          </p>
          {questions.length > 0 && (
            <button
              className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              onClick={() => setModalOpen(true)}
            >
              View all ({questions.length})
            </button>
          )}
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1">
                <div className="h-3.5 w-5/6 rounded bg-gray-200" />
                <div className="h-2.5 w-1/3 rounded bg-gray-100" />
              </div>
            ))}
          </div>
        ) : questions.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-500">No questions yet — ask your first question above.</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => handleAsk('')}>
              Ask a question
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 max-h-[280px] overflow-y-auto">
            {preview.map((q) => (
              <li
                key={q.id}
                className="group flex cursor-pointer items-start justify-between gap-3 py-3 first:pt-0 last:pb-0 hover:bg-gray-50 -mx-1 px-1 rounded-lg transition-colors"
                onClick={() => handleAsk(q.question)}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 leading-snug">{q.question}</p>
                  <p className="mt-0.5 text-xs text-gray-400">{formatTimestamp(q.asked_at)}</p>
                </div>
                <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-300 group-hover:text-gray-500" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {modalOpen && (
        <AllQuestionsModal
          questions={questions}
          workspaceId={workspaceId}
          onClose={() => setModalOpen(false)}
          onAsk={handleAsk}
        />
      )}
    </>
  );
};
